import { html, raw } from 'onefold';
import { CodeBlock } from '../../components/CodeBlock';
import { Callout } from '../../components/Callout';
import { Playground } from '../../components/Playground';

export function HttpClientPage(): Node {
  return html`
    <div>
      <h1>HTTP Client</h1>
      <p><code>createHttpClient</code> provides a typed HTTP client with interceptors, automatic JSON handling, and a clean API for <code>get</code>, <code>post</code>, <code>put</code>, <code>patch</code>, and <code>delete</code> methods.</p>

      <h2>Setup</h2>
      ${CodeBlock(`import { createHttpClient } from 'onefold/http';

const http = createHttpClient({
  baseUrl: 'https://api.example.com',
  headers: {
    'Content-Type': 'application/json',
  },
});`)}

      <h2>Making Requests</h2>
      ${CodeBlock(`// GET
const users = await http.get<User[]>('/users');

// POST
const newUser = await http.post<User>('/users', {
  body: { name: 'Alice', email: 'alice@example.com' },
});

// PUT
await http.put<User>('/users/1', {
  body: { name: 'Alice Updated' },
});

// PATCH
await http.patch<User>('/users/1', {
  body: { email: 'newemail@example.com' },
});

// DELETE
await http.delete('/users/1');`)}

      <h2>Options</h2>
      ${raw(`<table>
        <tr><th>Option</th><th>Type</th><th>Description</th></tr>
        <tr><td><code>baseUrl</code></td><td>string</td><td>Prepended to all request paths.</td></tr>
        <tr><td><code>headers</code></td><td>Record&lt;string, string&gt;</td><td>Default headers for every request.</td></tr>
        <tr><td><code>interceptors</code></td><td>Interceptors</td><td>Request/response/error hooks.</td></tr>
      </table>`)}

      <h2>Request Options</h2>
      ${CodeBlock(`const data = await http.get<User>('/users/1', {
  headers: { 'X-Custom': 'value' },  // merged with defaults
  signal: abortController.signal,     // AbortSignal for cancellation
});`)}

      ${Callout('All methods automatically serialize request bodies to JSON and parse JSON responses. Non-JSON responses return the raw Response object.')}

      <h2>Error Handling</h2>
      ${CodeBlock(`try {
  const user = await http.get<User>('/users/999');
} catch (err) {
  // err.status — HTTP status code
  // err.message — error message
  // err.data — parsed response body (if JSON)
}`)}

      <h2>With createResource</h2>
      ${CodeBlock(`import { createSignal, createResource } from 'onefold';

const userId = createSignal(1);
const user = createResource(userId, (id) => http.get<User>(\`/users/\${id}\`));

// user.data(), user.loading(), user.error() — all reactive`)}

      <h2>Try It</h2>
      <p>Fetches real data from JSONPlaceholder API using <code>createHttpClient</code>:</p>

      ${Playground(`import { createSignal, html, mount } from 'onefold';
import { createHttpClient } from 'onefold/http';

function App() {
  const http = createHttpClient({
    baseUrl: 'https://jsonplaceholder.typicode.com',
    headers: { 'Accept': 'application/json' },
  });

  const users = createSignal([]);
  const loading = createSignal(false);
  const error = createSignal('');

  async function fetchUsers() {
    loading.set(true);
    error.set('');
    try {
      const res = await http.get('/users');
      users.set(res.data);
    } catch (e) {
      error.set(e.message || 'Failed to fetch');
    }
    loading.set(false);
  }

  // Fetch on load
  fetchUsers();

  return html\`
    <div>
      <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:12px">
        <h3>Users (HTTP Client)</h3>
        <button onclick=\${fetchUsers} style="font-size:12px">Refetch</button>
      </div>
      \${() => loading() ? html\`<p style="color:#666">Loading...</p>\` : null}
      \${() => error() ? html\`<p style="color:#ef4444">\${error()}</p>\` : null}
      \${() => users().length > 0 ? html\`
        <div style="max-height:250px;overflow-y:auto">
          \${users().map(user => html\`
            <div style="display:flex;align-items:center;gap:12px;padding:10px 0;border-bottom:1px solid #f1f5f9">
              <div style="width:36px;height:36px;border-radius:50%;background:#e0e7ff;color:#4338ca;display:flex;align-items:center;justify-content:center;font-weight:600;font-size:13px">
                \${user.name.charAt(0)}
              </div>
              <div>
                <div style="font-weight:500;font-size:14px">\${user.name}</div>
                <div style="font-size:12px;color:#64748b">\${user.email}</div>
              </div>
            </div>
          \`)}
        </div>
      \` : null}
    </div>
  \`;
}

mount(App(), document.getElementById('app'));`, 'createHttpClient — Fetch Users', { allowNetwork: true })}

      <h2>Next Steps</h2>
      <ul>
        <li><a href="/data/interceptors">Interceptors</a> — transform requests and handle errors globally</li>
        <li><a href="/data/resource">Resource</a> — reactive async data fetching</li>
      </ul>
    </div>
  `;
}
